/**
 * CampaignEditDialog — post-creation edit of the "safe" campaign
 * fields (name, from-fields, provider, schedule, STO). For
 * already-sent campaigns, the user can force-edit metadata (name only,
 * effectively) with an explicit confirmation.
 *
 * Feb 2026 · R14.
 */
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, AlertTriangle } from "lucide-react";

const LOCKED_STATUSES = new Set(["sending", "sent"]);

export default function CampaignEditDialog({ wsId, campaign, onClose, onSaved }) {
  const isLocked = LOCKED_STATUSES.has(campaign.status);
  const [name, setName] = useState(campaign.name || "");
  const [fromName, setFromName] = useState(campaign.from_name || "");
  const [fromEmail, setFromEmail] = useState(campaign.from_email || "");
  const [fromNumber, setFromNumber] = useState(campaign.from_number || "");
  const [scheduleAt, setScheduleAt] = useState(
    campaign.schedule_at ? campaign.schedule_at.slice(0, 16) : ""
  );
  const [provider, setProvider] = useState(campaign.provider || "resend");
  const [sto, setSto] = useState(!!campaign.send_time_optimization);
  const [force, setForce] = useState(false);
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [segId, setSegId] = useState(campaign.segment_id || "");
  const [tmplId, setTmplId] = useState(campaign.template_id || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/marketing/segments`).then(r => setSegments(r.data || []));
    api.get(`/workspaces/${wsId}/marketing/templates?channel=${campaign.channel}`).then(r => setTemplates(r.data || []));
  }, [wsId, campaign.channel]);

  const save = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (isLocked && !force) return toast.error("Campaign already sent — check the 'force edit' box to update metadata");
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        from_name: fromName || null,
        from_email: fromEmail || null,
        from_number: fromNumber || null,
        provider,
        send_time_optimization: sto,
        force: isLocked ? true : undefined,
      };
      if (scheduleAt) payload.schedule_at = new Date(scheduleAt).toISOString();
      if (segId && segId !== campaign.segment_id) payload.segment_id = segId;
      if (tmplId && tmplId !== campaign.template_id) payload.template_id = tmplId;
      await api.patch(`/workspaces/${wsId}/marketing/campaigns/${campaign.id}`, payload);
      toast.success("Campaign updated");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-primary" /> Edit campaign
          </DialogTitle>
        </DialogHeader>
        {isLocked && (
          <div className="border border-amber-400 bg-amber-50 p-3 text-xs flex items-start gap-2" data-testid="campaign-edit-locked-warning">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              This campaign is <b>{campaign.status}</b>. Metadata edits will save but <b>won&apos;t re-dispatch messages</b> or change what was already sent. Segment/template/schedule fields can still be changed for reporting purposes only.
              <label className="flex items-center gap-1 mt-2 cursor-pointer">
                <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="campaign-edit-force" />
                I understand — allow the edit
              </label>
            </div>
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-none" data-testid="campaign-edit-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Provider</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger className="h-9 rounded-none" data-testid="campaign-edit-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {campaign.channel === "email" && <SelectItem value="resend">Resend</SelectItem>}
                  {campaign.channel === "whatsapp" && <SelectItem value="interakt">Interakt</SelectItem>}
                  {campaign.channel === "whatsapp" && <SelectItem value="twilio_wa">Twilio WA</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Schedule at</Label>
              <Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="h-9 rounded-none" data-testid="campaign-edit-schedule" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Segment</Label>
            <Select value={segId} onValueChange={setSegId}>
              <SelectTrigger className="h-9 rounded-none" data-testid="campaign-edit-segment"><SelectValue /></SelectTrigger>
              <SelectContent>
                {segments.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Template</Label>
            <Select value={tmplId} onValueChange={setTmplId}>
              <SelectTrigger className="h-9 rounded-none" data-testid="campaign-edit-template"><SelectValue /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">From name</Label>
              <Input value={fromName} onChange={(e) => setFromName(e.target.value)} className="h-9 rounded-none" />
            </div>
            {campaign.channel === "email" && (
              <div className="col-span-2">
                <Label className="text-xs">From email</Label>
                <Input type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} className="h-9 rounded-none" />
              </div>
            )}
            {campaign.channel === "whatsapp" && (
              <div className="col-span-2">
                <Label className="text-xs">From number</Label>
                <Input value={fromNumber} onChange={(e) => setFromNumber(e.target.value)} className="h-9 rounded-none" placeholder="+91..." />
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={sto} onChange={(e) => setSto(e.target.checked)} />
            Send-time optimization
          </label>
          <div className="text-[10px] text-muted-foreground">
            Created {campaign.created_at?.slice(0, 10)} · Channel {campaign.channel} · Status <b>{campaign.status}</b>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy || (isLocked && !force)} data-testid="campaign-edit-save">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
